// HR Workspace Store - AgentBheem ERP
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type CanvasType =
  | 'dashboard'
  | 'employees'
  | 'employee-workspace'
  | 'employee-page'
  | 'employee-profile'
  | 'attendance'
  | 'leaves'
  | 'payroll'
  | 'payslips'
  | 'recruitment'
  | 'candidates'
  | 'statutory'
  | 'settings'

interface SelectedEntity {
  type: 'employee' | 'candidate' | 'leave-request' | 'payslip' | 'job'
  id: string
  data?: Record<string, unknown>
}

interface WorkspaceState {
  // Sidebar states
  isSidebarCollapsed: boolean
  isChatHistoryOpen: boolean
  isAIChatOpen: boolean
  isMobileMenuOpen: boolean

  // Active content
  activeCanvas: CanvasType
  selectedEntity: SelectedEntity | null

  // Command palette
  isCommandPaletteOpen: boolean

  // Actions
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleChatHistory: () => void
  setChatHistoryOpen: (open: boolean) => void
  toggleAIChat: () => void
  setAIChatOpen: (open: boolean) => void
  toggleMobileMenu: () => void
  setMobileMenuOpen: (open: boolean) => void

  setActiveCanvas: (canvas: CanvasType) => void
  setSelectedEntity: (entity: SelectedEntity | null) => void

  toggleCommandPalette: () => void
  setCommandPaletteOpen: (open: boolean) => void

  // Navigate helper
  navigateToEmployee: (id: string, data?: Record<string, unknown>) => void
  navigateToCandidate: (id: string, data?: Record<string, unknown>) => void
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set) => ({
      // Initial states
      isSidebarCollapsed: false,
      isChatHistoryOpen: true,
      isAIChatOpen: true,
      isMobileMenuOpen: false,
      activeCanvas: 'dashboard',
      selectedEntity: null,
      isCommandPaletteOpen: false,

      // Sidebar actions
      toggleSidebar: () => set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),

      // Chat history actions
      toggleChatHistory: () => set((state) => ({ isChatHistoryOpen: !state.isChatHistoryOpen })),
      setChatHistoryOpen: (open) => set({ isChatHistoryOpen: open }),

      // AI chat actions
      toggleAIChat: () => set((state) => ({ isAIChatOpen: !state.isAIChatOpen })),
      setAIChatOpen: (open) => set({ isAIChatOpen: open }),

      // Mobile menu actions
      toggleMobileMenu: () => set((state) => ({ isMobileMenuOpen: !state.isMobileMenuOpen })),
      setMobileMenuOpen: (open) => set({ isMobileMenuOpen: open }),

      // Canvas actions
      setActiveCanvas: (canvas) => set({ activeCanvas: canvas, selectedEntity: null }),
      setSelectedEntity: (entity) => set({ selectedEntity: entity }),

      // Command palette actions
      toggleCommandPalette: () => set((state) => ({ isCommandPaletteOpen: !state.isCommandPaletteOpen })),
      setCommandPaletteOpen: (open) => set({ isCommandPaletteOpen: open }),

      // Navigation helpers
      navigateToEmployee: (id, data) => set({
        activeCanvas: 'employee-profile',
        selectedEntity: { type: 'employee', id, data },
      }),
      navigateToCandidate: (id, data) => set({
        activeCanvas: 'candidates',
        selectedEntity: { type: 'candidate', id, data },
      }),
    }),
    {
      name: 'hr-workspace-storage',
      partialize: (state) => ({
        isSidebarCollapsed: state.isSidebarCollapsed,
        isChatHistoryOpen: state.isChatHistoryOpen,
        isAIChatOpen: state.isAIChatOpen,
      }),
    }
  )
)
